import { Injectable, inject, DestroyRef } from '@angular/core';
import { Router, NavigationError, NavigationEnd } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export const CHUNK_RELOAD_STORAGE_KEY = 'chunk_reload_guard';

/**
 * Detects whether a navigation error was caused by a dynamic import / chunk fetch failure
 * across Chrome/Chromium, Firefox, and Safari/WebKit browser engines.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const check = (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    const lower = value.toLowerCase();
    return (
      lower.includes('failed to fetch dynamically imported module') ||
      lower.includes('error loading dynamically imported module') ||
      lower.includes('loading dynamically imported module') ||
      lower.includes('importing a module script failed')
    );
  };

  if (typeof error === 'string') {
    return check(error);
  }

  if (typeof error === 'object' && error !== null) {
    const err = error as Record<string, unknown>;
    if (check(err['message'])) return true;
    if (check(err['stack'])) return true;
    if (err['originalError'] && isChunkLoadError(err['originalError'])) return true;
    if (err['rejection'] && isChunkLoadError(err['rejection'])) return true;
    if (err['cause'] && isChunkLoadError(err['cause'])) return true;
  }

  return false;
}

@Injectable({
  providedIn: 'root',
})
export class ChunkReloadService {
  private router = inject(Router);
  private document = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef, { optional: true });

  constructor() {
    this.init();
  }

  private init(): void {
    const events$ = this.destroyRef
      ? this.router.events.pipe(takeUntilDestroyed(this.destroyRef))
      : this.router.events;

    events$.subscribe((event) => {
      if (event instanceof NavigationError) {
        this.handleNavigationError(event);
      } else if (event instanceof NavigationEnd) {
        this.handleNavigationEnd();
      }
    });

    this.initWindowListeners();
  }

  // Dynamic imports that don't run through the Router (on-demand `import()`
  // calls, e.g. the ngx-echarts / exceljs lazy loaders) surface as
  // unhandledrejection/error events instead of a Router NavigationError, so
  // they need their own guarded-reload path (PLU-300).
  private initWindowListeners(): void {
    const win = this.document.defaultView ?? (typeof window !== 'undefined' ? window : null);
    if (!win) {
      return;
    }

    const onRejection = (event: PromiseRejectionEvent): void => {
      this.handleWindowError(event.reason, win.location.href);
    };
    const onError = (event: ErrorEvent): void => {
      this.handleWindowError(event.error ?? event.message, win.location.href);
    };

    win.addEventListener('unhandledrejection', onRejection);
    win.addEventListener('error', onError);

    this.destroyRef?.onDestroy(() => {
      win.removeEventListener('unhandledrejection', onRejection);
      win.removeEventListener('error', onError);
    });
  }

  private handleWindowError(error: unknown, currentUrl: string): void {
    if (!isChunkLoadError(error)) {
      return;
    }
    this.guardedReload(currentUrl);
  }

  private handleNavigationError(event: NavigationError): void {
    if (!isChunkLoadError(event.error)) {
      return;
    }
    this.guardedReload(event.url);
  }

  private guardedReload(url: string): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    const isGuardSet = storage.getItem(CHUNK_RELOAD_STORAGE_KEY) === 'true';
    if (isGuardSet) {
      console.warn(
        `[ChunkReloadService] Dynamic chunk load failed again for ${url} with guard set. Suppressing reload to prevent loop.`
      );
      return;
    }

    storage.setItem(CHUNK_RELOAD_STORAGE_KEY, 'true');
    this.reload(url);
  }

  private handleNavigationEnd(): void {
    const storage = this.getStorage();
    if (storage?.getItem(CHUNK_RELOAD_STORAGE_KEY)) {
      storage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
    }
  }

  protected getStorage(): Storage | null {
    try {
      return (
        this.document.defaultView?.sessionStorage ??
        (typeof sessionStorage !== 'undefined' ? sessionStorage : null)
      );
    } catch {
      return null;
    }
  }

  protected reload(url: string): void {
    const win =
      this.document.defaultView ??
      (typeof window !== 'undefined' ? window : null);
    if (win?.location?.assign) {
      win.location.assign(url);
    } else if (win?.location) {
      win.location.href = url;
    }
  }
}
